import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, KeyRound, Lock, ArrowLeft, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import BrandMark from "../components/BrandMark";

/**
 * 3-step account recovery page.
 *
 * Flow:
 *   1. Enter email          → POST /auth/forgot-password (always 200)
 *   2. Enter 6-digit code   → kept local until step 3 submits
 *   3. Enter new password   → POST /auth/reset-password { email, code, new_password }
 *
 * We never tell the user whether an email is registered (matches the
 * backend's enumeration-safe response). On success we redirect to /auth.
 */
const STEPS = { EMAIL: 1, CODE: 2, PASSWORD: 3 };

export default function RecoverPage() {
  const nav = useNavigate();
  const [step, setStep] = useState(STEPS.EMAIL);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const requestCode = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) return toast.error("Enter a valid email");
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      toast.success("If we have your email, a 6-digit code is on its way.");
      setStep(STEPS.CODE);
    } catch {
      // Backend doesn't error on unknown emails — only on rate limiting / 500
      toast.error("Something went wrong. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    if (code.length !== 6 || !/^\d{6}$/.test(code)) return toast.error("Enter the 6-digit code");
    if (newPassword.length < 8) return toast.error("Password must be at least 8 characters");
    if (newPassword !== confirm) return toast.error("Passwords don't match");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", {
        email: email.trim().toLowerCase(),
        code,
        new_password: newPassword,
      });
      toast.success("Password updated — sign in with your new password.");
      nav("/auth");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't reset password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <Link
          to="/auth"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-white/50 hover:text-white mb-8"
          data-testid="recover-back"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
        </Link>

        <div className="mb-8">
          <BrandMark size={28} />
        </div>

        <div className="label-eyebrow text-[#6B9BFF] mb-3">Account recovery</div>
        <h1 className="font-display text-4xl sm:text-5xl leading-none mb-8">
          {step === STEPS.EMAIL && "Forgot your password?"}
          {step === STEPS.CODE && "Check your inbox."}
          {step === STEPS.PASSWORD && "Set a new password."}
        </h1>

        {/* Step 1 — email */}
        {step === STEPS.EMAIL && (
          <form onSubmit={requestCode} className="space-y-5" data-testid="recover-step-email">
            <p className="text-white/65 text-sm mb-6">
              Enter your email and we&apos;ll send you a 6-digit code to reset your password.
            </p>
            <div>
              <div className="label-eyebrow text-white/50 mb-2">Email</div>
              <div className="flex items-center gap-3 border border-white/10 bg-[#121212] px-4">
                <Mail className="w-4 h-4 text-white/40" />
                <input
                  type="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 bg-transparent py-3.5 focus:outline-none"
                  placeholder="you@example.com"
                  data-testid="recover-email-input"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={busy}
              className="w-full bg-[#6B9BFF] text-black font-bold uppercase tracking-widest text-xs py-4 hover:bg-white disabled:opacity-50 transition-colors inline-flex items-center justify-center gap-2"
              data-testid="recover-send-code"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Send reset code
            </button>
          </form>
        )}

        {/* Steps 2 + 3 — code + password (combined form because the user
            usually has both pieces of info ready once they open the email) */}
        {step !== STEPS.EMAIL && (
          <form onSubmit={submitReset} className="space-y-5" data-testid="recover-step-reset">
            <p className="text-white/65 text-sm mb-6">
              We sent a 6-digit code to <span className="text-white font-bold">{email}</span>.
              It expires in 15 minutes.
            </p>
            <div>
              <div className="label-eyebrow text-white/50 mb-2">6-digit code</div>
              <div className="flex items-center gap-3 border border-white/10 bg-[#121212] px-4">
                <KeyRound className="w-4 h-4 text-white/40" />
                <input
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  autoFocus
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="flex-1 bg-transparent py-3.5 focus:outline-none font-mono tracking-[0.4em] text-lg"
                  placeholder="000000"
                  data-testid="recover-code-input"
                />
              </div>
            </div>

            <div>
              <div className="label-eyebrow text-white/50 mb-2">New password</div>
              <div className="flex items-center gap-3 border border-white/10 bg-[#121212] px-4">
                <Lock className="w-4 h-4 text-white/40" />
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="flex-1 bg-transparent py-3.5 focus:outline-none"
                  placeholder="At least 8 characters"
                  data-testid="recover-newpw-input"
                />
              </div>
            </div>

            <div>
              <div className="label-eyebrow text-white/50 mb-2">Confirm new password</div>
              <div className="flex items-center gap-3 border border-white/10 bg-[#121212] px-4">
                <Lock className="w-4 h-4 text-white/40" />
                <input
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="flex-1 bg-transparent py-3.5 focus:outline-none"
                  placeholder="Re-enter password"
                  data-testid="recover-confirm-input"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={busy}
              className="w-full bg-[#6B9BFF] text-black font-bold uppercase tracking-widest text-xs py-4 hover:bg-white disabled:opacity-50 transition-colors inline-flex items-center justify-center gap-2"
              data-testid="recover-submit"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Reset password
            </button>

            <button
              type="button"
              onClick={() => { setStep(STEPS.EMAIL); setCode(""); setNewPassword(""); setConfirm(""); }}
              className="w-full text-xs uppercase tracking-widest text-white/50 hover:text-white"
              data-testid="recover-back-to-email"
            >
              ← Use a different email
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
